import React, {useRef, useEffect} from 'react';
import {useFormikContext} from 'formik';
import {MaterialFormFieldOptions} from 'shared/types/materials';
import {ColorSwatchSelected} from 'components/customer/Room/ColorSwatches';
import {MaterialType} from 'components/customer/Materials/entity';
import {Page} from 'store/customer/entity/Page';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {setProductDefaultTabErrorMapping} from 'components/customer/Room/store/roomErrorSlice';
import {WizardComponent} from 'shared/components/Wizard/entity/WizardComponent';
import {RoomFields} from 'components/customer/Room/RoomComponents';
import styled from 'styled-components';
import {MaterialsContainer} from 'components/customer/Product/Specs/MaterialSimplified';
import {DoorStyle} from 'components/customer/Product/Specs/Materials/DoorStyle';
import {Material} from 'components/customer/Product/Specs/Materials/Material';
import {MaterialSearch} from 'components/customer/Product/Specs/Materials/MaterialSearch';
import {Col, Row} from 'react-bootstrap';
import {
    selectLeftSidebar,
    selectLeftSidebarBehaviour,
    selectRightSidebar,
    selectRightSidebarBehaviour,
    SidebarBehaviour,
    SidebarStatus,
} from 'store/customer/UISlice';
import {selectIsLoadingMaterials} from 'components/customer/Materials/store/materialSlice';
import {Loader} from 'shared/helpers';

export interface MaterialSearchStatus {
    carcase: boolean;
    exterior: boolean;
}

interface ColourSearchInterface extends WizardComponent {
    materialType: MaterialType;
    materialPage: Page;
    eventKey: number;
    isMiniEditor?: boolean;
}

const ColourSearch = ({
    materialType,
    materialPage,
    eventKey,
    isMiniEditor = false,
}: ColourSearchInterface): JSX.Element => {
    const dispatch = useAppDispatch();

    const materialsLoading = useAppSelector(selectIsLoadingMaterials);

    const leftSidebarBehaviour = useAppSelector(selectLeftSidebarBehaviour);
    const leftSidebarStatus = useAppSelector(selectLeftSidebar);

    const rightSidebarBehaviour = useAppSelector(selectRightSidebarBehaviour);
    const rightSidebarStatus = useAppSelector(selectRightSidebar);

    const isLeftSidebarOpen =
        leftSidebarBehaviour == SidebarBehaviour.TOGGLE &&
        leftSidebarStatus == SidebarStatus.OPEN;
    const isRightSidebarOpen =
        rightSidebarBehaviour == SidebarBehaviour.TOGGLE &&
        rightSidebarStatus == SidebarStatus.OPEN;

    const {values} = useFormikContext<RoomFields>();

    const field = useRef<MaterialFormFieldOptions>({
        options: {
            horizontalGrainFieldName:
                materialType == MaterialType.EXTERIOR
                    ? 'exteriorColorHorizontal'
                    : 'carcaseColorHorizontal',
            doubleSidedFieldName:
                materialType == MaterialType.EXTERIOR
                    ? 'exteriorColorDoubleSided'
                    : 'carcaseColorDoubleSided',
            customColourFieldName:
                materialType == MaterialType.EXTERIOR
                    ? 'exteriorColorCustomName'
                    : 'carcaseColorCustomName',
        },
    });

    useEffect(() => {
        let payload = {};
        if (materialType == MaterialType.EXTERIOR)
            payload = {
                [eventKey]: {
                    tabName: 'exteriorColourSelections',
                    fields: ['exteriorColorCustomName'],
                },
            };
        else
            payload = {
                [eventKey]: {
                    tabName: 'carcaseColourSelections',
                    fields: ['carcaseColorCustomName'],
                },
            };
        dispatch(setProductDefaultTabErrorMapping(payload));
    }, [eventKey, materialType]);

    return (
        <div className="relativePos">
            <Loader loader={materialsLoading} />
            <Row>
                <Col
                    sm={
                        isMiniEditor
                            ? {}
                            : {
                                  offset:
                                      isLeftSidebarOpen && isRightSidebarOpen
                                          ? 1
                                          : 2,
                                  span:
                                      isLeftSidebarOpen && isRightSidebarOpen
                                          ? 10
                                          : 8,
                              }
                    }
                    xs={{offset: 0, span: 12}}>
                    <MaterialsContainer
                        $isMiniBrowser={isMiniEditor}
                        $isExterior={
                            (isMiniEditor &&
                                materialType == MaterialType.EXTERIOR) ||
                            false
                        }>
                        {materialType == MaterialType.EXTERIOR ? (
                            <DoorStyle
                                isExterior={
                                    materialType == MaterialType.EXTERIOR
                                }
                                isMiniBrowser={isMiniEditor}
                                productInfo={{hasDoors: true, hasDrawers: true}}
                                page={materialPage}
                            />
                        ) : null}

                        <Material
                            isMiniBrowser={isMiniEditor}
                            isExterior={materialType == MaterialType.EXTERIOR}
                            thumb={
                                <ColorSwatchSelected
                                    containerClass=".col-md-2"
                                    isExterior={
                                        materialType == MaterialType.EXTERIOR
                                    }
                                    horizontal={
                                        materialType == MaterialType.EXTERIOR
                                            ? values.exteriorColorHorizontal
                                            : values.carcaseColorHorizontal
                                    }
                                />
                            }
                            productInfo={{hasDoors: true, hasDrawers: true}}
                            hasBothMaterials={true}
                            page={materialPage}
                        />
                        <MaterialSearch
                            isExterior={materialType == MaterialType.EXTERIOR}
                            field={field.current}
                            isMiniBrowser={isMiniEditor}
                            productInfo={{hasDoors: true, hasDrawers: true}}
                            page={materialPage}
                        />
                    </MaterialsContainer>
                </Col>
            </Row>
        </div>
    );
};

export const ColorSwatchContainer = styled.div<{
    $isMobile?: boolean;
    $isMiniBrowser?: boolean;
}>`
    display: ${({$isMobile, $isMiniBrowser}) =>
        $isMobile || $isMiniBrowser ? 'flex' : 'initial'};
    justify-content: center;
    margin-bottom: ${({$isMobile}) => ($isMobile ? '10px' : '0')};

    ${({$isMiniBrowser}) => {
        if ($isMiniBrowser) {
            return `
                flex-direction: column;
                align-items: center;
            `;
        }
    }}

    // Mobile devices
    @media screen and (max-width: 768px) and (orientation: portrait),
        screen and (max-width: 1024px) and (orientation: landscape) {
        gap: 10px;
        justify-content: space-between;
        align-items: center;
    }
`;

export default ColourSearch;
